import React, { useState } from "react";

import {
  useGetCurrentCompanyApplicationQuery,
  useUpdateCompanyApplicationMutation,
  useUpdateUserExperienceFlagsMutation,
  useGetUserExperienceFlagsQuery,
} from "EmployerApplication/api";
import { UNSUPPORTED_DISABILITY_SIC_CODES } from "EmployerApplication/Components/Constants";
import { isRenewal } from "QuotePage/urls";
import { useSelector } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import { TitleSmall } from "Cobalt/Header";
import { Modal, ModalHeader } from "Cobalt/Modal";
import { getCart } from "Common/CartService";

import BusinessTenurePage from "./DisabilityPlansModalPages/BusinessTenurePage";
import { DonePage } from "./DisabilityPlansModalPages/DonePage";
import { PlansPage } from "./DisabilityPlansModalPages/PlansPage";
import { SalaryPage } from "./DisabilityPlansModalPages/SalaryPage";
import { SICPage } from "./DisabilityPlansModalPages/SicPage";

import makeBem from "Utils/makeBem";

const bem = makeBem("AddAncillaryPlanModal");

const ANALYTICS_PAGE_MAP = Object.freeze({
  businessTenurePage: "company_age",
  sicPage: "SIC",
  salaryPage: "census",
  plansPage: "plans",
  donePage: "done",
});

const location = "short_term_disability_modal";

const ShortTermDisabilityPlansModal = ({ show, onHide }) => {
  const [currentPage, setCurrentPage] = useState(null);

  const [updateCompanyApplication] = useUpdateCompanyApplicationMutation();
  const [updateUserExperienceFlags] = useUpdateUserExperienceFlagsMutation();

  const { data: companyApplication, isLoading } = useGetCurrentCompanyApplicationQuery();
  const companyPolicyId = companyApplication?.id;
  const companyIDToken = useSelector((state) => state.companyProfile?.idToken);

  const { data: userExperienceFlags } = useGetUserExperienceFlagsQuery({
    companyIDToken,
  });

  const trackPageView = (pageName) => {
    AnalyticsTracking.trackEvent(`disability_${ANALYTICS_PAGE_MAP[pageName]}_viewed`, {
      location,
    });
  };

  const updateCurrentPage = (pageName) => {
    trackPageView(pageName);
    setCurrentPage(pageName);
  };

  const handleSubmitOnClose = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      label: "disability_modal_closed",
      location,
    });
  };

  // for renwals, we should prefill the sic code if the user has already entered it only if it was entered using the sic code modal
  // this will allow more specific sic codes to be entered.
  const shouldPrefillSICCode = (isRenewal() && userExperienceFlags?.life_plan_modal_sic_code_entered) || (!isRenewal() && companyApplication?.sicCode);

  const sicSubmitHandler = async (values) => {
    if (!companyPolicyId) return;
    await updateUserExperienceFlags({
      companyIDToken,
      life_plan_modal_sic_code_entered: true,
    });

    const unsupportedSICCode = UNSUPPORTED_DISABILITY_SIC_CODES.includes(values?.company_application?.sic_code);

    values.company_application.eligible_for_disability = !unsupportedSICCode;
    await updateCompanyApplication({ id: companyPolicyId, ...values });

    if (unsupportedSICCode) {
      onHide();
    } else {
      updateCurrentPage("salaryPage");
    }
  };

  let sicInitialValues;

  if (!isLoading) {
    sicInitialValues = {
      company_application: {
        business_category: companyApplication.businessCategory || undefined,
        sic_code: companyApplication.sicCode || undefined,
      },
    };
  }

  const { short_term_disability: shortTermPlanId, long_term_disability: longTermPlanId } = useSelector(getCart);

  const eligibleForDisability = companyApplication?.eligibleForDisability;

  const getInitialPage = () => {
    if (shortTermPlanId || longTermPlanId) {
      return "plansPage";
    }
    if (eligibleForDisability === true && !userExperienceFlags?.life_plan_modal_sic_code_entered) {
      return "sicPage";
    }
    if (eligibleForDisability === true && userExperienceFlags?.life_plan_modal_sic_code_entered) {
      return "salaryPage";
    }
    return "businessTenurePage";
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      onShow={() => {
        const firstPage = getInitialPage();
        updateCurrentPage(firstPage);
      }}
      onEscapeKeyDown={() => {
        handleSubmitOnClose();
        onHide();
      }}
      onExited={() => {
        handleSubmitOnClose();
        onHide();
      }}
    >
      <ModalHeader closeButton>
        <TitleSmall mt={0} mb={0}>
          Add Short Term Disability Plan
        </TitleSmall>
      </ModalHeader>
      {currentPage === "plansPage" ? (
        <PlansPage pageName="plansPage" setCurrentPage={updateCurrentPage} onHide={onHide} disabilityInsuranceType="short-term" />
      ) : currentPage === "salaryPage" ? (
        <SalaryPage setCurrentPage={updateCurrentPage} />
      ) : currentPage === "sicPage" ? (
        <SICPage
          pageName="sicPage"
          setCurrentPage={updateCurrentPage}
          submitHandler={sicSubmitHandler}
          initialValues={sicInitialValues}
          onHide={onHide}
          shouldPrefillSICCode={shouldPrefillSICCode}
        />
      ) : currentPage === "donePage" ? (
        <DonePage onHide={onHide} />
      ) : (
        <BusinessTenurePage pageName="businessTenurePage" setCurrentPage={updateCurrentPage} onHide={onHide} />
      )}
    </Modal>
  );
};

export default ShortTermDisabilityPlansModal;
